import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../../utils/api';
import { Send, Bell, Users as UsersIcon, AlertCircle, History } from 'lucide-react';
import { toast } from 'sonner';

/**
 * AdminPushTab (iter63)
 * Lets admins broadcast a web push notification to all subscribed users (or a specific user).
 * Re-uses the existing VAPID web-push pipeline. History of past broadcasts is shown below
 * the compose form for accountability.
 */
const AdminPushTab = () => {
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ title: '', body: '', url: '/', target: 'all', target_user_id: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, h] = await Promise.all([adminAPI.pushStats(), adminAPI.pushHistory()]);
      setStats(s.data);
      setHistory(Array.isArray(h.data) ? h.data : []);
    } catch (e) {
      toast.error('Failed to load push data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSend = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      toast.error('Title and body are required');
      return;
    }
    if (form.target === 'user' && !form.target_user_id.trim()) {
      toast.error('Enter a user_id to target');
      return;
    }
    const target = form.target === 'user' ? `user:${form.target_user_id.trim()}` : 'all';
    setSending(true);
    try {
      const { data } = await adminAPI.pushBroadcast(form.title.trim(), form.body.trim(), form.url || '/', target);
      toast.success(`Sent: ${data.sent} · Recipients: ${data.recipients}`);
      setForm({ title: '', body: '', url: '/', target: 'all', target_user_id: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Broadcast failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-push-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-base sm:text-lg font-bold text-[#0A0B10] flex items-center gap-2">
          <Bell size={18} className="text-[#1800ad]" />
          Push Notifications
        </h2>
        {stats && (
          <div className="text-xs text-[#6B7280] flex items-center gap-3">
            <span className="flex items-center gap-1"><UsersIcon size={12} /> {stats.unique_subscribers} subscriber{stats.unique_subscribers !== 1 ? 's' : ''}</span>
            <span>·</span>
            <span>{stats.subscriptions} device{stats.subscriptions !== 1 ? 's' : ''}</span>
          </div>
        )}
      </div>

      {stats && !stats.vapid_configured && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2 items-start" data-testid="vapid-warning">
          <AlertCircle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800">
            VAPID keys are not configured on the server. Add <code className="font-mono">VAPID_PUBLIC_KEY</code>, <code className="font-mono">VAPID_PRIVATE_KEY</code>, and <code className="font-mono">VAPID_SUBJECT</code> to <code className="font-mono">backend/.env</code> and redeploy to enable push notifications.
          </p>
        </div>
      )}

      {/* Compose form */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
        <h3 className="text-sm font-semibold text-[#0A0B10]">Compose broadcast</h3>

        <div>
          <label className="block text-[11px] font-semibold text-[#6B7280] mb-1">Title</label>
          <input
            type="text"
            data-testid="push-title-input"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="e.g. New listings near you"
            maxLength={80}
            className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-[#0A0B10] focus:border-[#1800ad] focus:ring-2 focus:ring-[#1800ad]/10 outline-none"
          />
          <p className="text-[10px] text-[#9CA3AF] mt-1">{form.title.length}/80</p>
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-[#6B7280] mb-1">Body</label>
          <textarea
            data-testid="push-body-input"
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            placeholder="Tap to explore the latest deals on GenzoX"
            maxLength={200}
            rows={3}
            className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-[#0A0B10] focus:border-[#1800ad] focus:ring-2 focus:ring-[#1800ad]/10 outline-none resize-none"
          />
          <p className="text-[10px] text-[#9CA3AF] mt-1">{form.body.length}/200</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-[11px] font-semibold text-[#6B7280] mb-1">Click-through URL</label>
            <input
              type="text"
              data-testid="push-url-input"
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder="/"
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-[#0A0B10] focus:border-[#1800ad] focus:ring-2 focus:ring-[#1800ad]/10 outline-none"
            />
            <p className="text-[10px] text-[#9CA3AF] mt-1">Where users land when they tap the notification.</p>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-[#6B7280] mb-1">Audience</label>
            <select
              data-testid="push-target-select"
              value={form.target}
              onChange={(e) => setForm({ ...form, target: e.target.value })}
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-[#0A0B10] focus:border-[#1800ad] focus:ring-2 focus:ring-[#1800ad]/10 outline-none"
            >
              <option value="all">All subscribers</option>
              <option value="user">Single user (by user_id)</option>
            </select>
          </div>
        </div>

        {form.target === 'user' && (
          <div>
            <label className="block text-[11px] font-semibold text-[#6B7280] mb-1">Target user_id</label>
            <input
              type="text"
              data-testid="push-target-userid"
              value={form.target_user_id}
              onChange={(e) => setForm({ ...form, target_user_id: e.target.value })}
              placeholder="user_abcdef1234"
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-[#0A0B10] focus:border-[#1800ad] focus:ring-2 focus:ring-[#1800ad]/10 outline-none font-mono"
            />
          </div>
        )}

        <div className="flex justify-end pt-1">
          <button
            type="button"
            data-testid="push-send-btn"
            onClick={onSend}
            disabled={sending}
            className="inline-flex items-center gap-2 bg-[#1800ad] hover:bg-[#1400a0] text-white rounded-lg px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-50"
          >
            <Send size={14} />
            {sending ? 'Sending…' : 'Send broadcast'}
          </button>
        </div>
      </div>

      {/* History */}
      <div className="bg-white rounded-xl border border-gray-100">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
          <History size={14} className="text-[#6B7280]" />
          <h3 className="text-sm font-semibold text-[#0A0B10]">Recent broadcasts</h3>
        </div>
        {loading ? (
          <div className="px-4 py-6 text-center text-sm text-[#9CA3AF]">Loading…</div>
        ) : history.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-[#9CA3AF]">No broadcasts sent yet.</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {history.map((h) => (
              <div key={h.broadcast_id} className="px-4 py-3" data-testid={`broadcast-${h.broadcast_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-[#0A0B10] truncate">{h.title}</p>
                    <p className="text-xs text-[#6B7280] mt-0.5 line-clamp-2">{h.body}</p>
                    <p className="text-[10px] text-[#9CA3AF] mt-1.5">
                      {new Date(h.created_at).toLocaleString()} · target: <span className="font-mono">{h.target}</span> · by {h.sent_by_email}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs font-semibold text-[#1800ad]">{h.sent} sent</p>
                    <p className="text-[10px] text-[#9CA3AF]">{h.recipient_count} target{h.recipient_count !== 1 ? 's' : ''}</p>
                    {h.removed > 0 && <p className="text-[10px] text-amber-600">{h.removed} expired</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPushTab;
